import { PREVIOUS_USER_KEY, USER_KEY } from "@/constants";
import { useUserContext } from "@/context/UserContext";
import * as React from "react";

/**
 * Remembered per-table display choices — which view, which columns.
 *
 * Opt in with an explicit `tableId`. Nothing is derived from the headers or the
 * URL, and that is deliberate: every derived key that looked plausible is
 * broken. `headerKey` is built from the *visible* headers, so hiding a column
 * would rename the key and orphan the preference that hid it; it also encodes
 * widths, so a width change would silently reset everybody; and the pathname
 * carries `:id` route params, so Line Items would remember a different set of
 * columns per campaign. The one thing a derived key would buy — two all-width
 * tables on the same page telling themselves apart — does not occur anywhere in
 * this repo.
 *
 * Namespaced by the identity the UI is actually showing, not just the account
 * id. `loginAsAdvertiser` spreads `{...user, asAdvertiser}`, so `user.id` does
 * NOT change while impersonating an advertiser — and it swaps `$permissions`,
 * so the header set genuinely differs. Without the advertiser in the key an
 * agency user's 13-column layout follows them into a restricted view that has
 * no such columns. A logged-out table (the lab) shares one `anon` bucket.
 *
 * These are display choices, which is the only reason they belong in
 * localStorage at all: OWASP's line is that Web Storage may not hold anything
 * an authentication decision rests on, since one XSS both reads and WRITES it.
 * Nothing here is an access-control mechanism — `CustomTable` applies the
 * permission filter to `permittedHeaders` BEFORE this list narrows it, so a
 * hand-edited record can only hide a column from yourself, never reveal one.
 * The other half of that rule is honoured in `read()`: what comes back out is
 * untrusted input.
 *
 * Device-local, so a layout set on the laptop does not exist on the desktop.
 * Fine for an ops dashboard; if that changes, everything server-side would need
 * is behind this one hook.
 */

const PREFIX = "contanuity.table";

/**
 * Bumped when the stored shape changes in a way `read()` cannot simply validate
 * its way out of. A record from a different version is discarded rather than
 * half-read — add a migration here instead if the layout is worth carrying over.
 * Records written before versioning existed carry the v1 shape, so a missing
 * `v` is read as 1 and nobody loses their columns to this.
 */
const VERSION = 1;

/** The logged-out bucket. Not an account, and never swept. */
const ANON = "anon";

export type TableView = "compact" | "full" | "fit";
export type TableCellDisplay = "truncate" | "wrap" | "fit";
export type TableDensity = "comfortable" | "compact";

export interface TablePreferences {
  view?: TableView;
  hiddenColumns?: string[];
}

const VIEWS: TableView[] = ["compact", "full", "fit"];

/**
 * How far a name may run before it is cut. Three lines is the point where a
 * wrapped row still reads as one row: at four the table starts to look like a
 * list of cards, and one long value in a ten-row page adds a screenful.
 */
export const FULL_VIEW_MAX_LINES = 3;

/**
 * The one place a named view becomes layout. `CustomTable` takes the low-level
 * props too, which is how the lab compares settings that aren't shipping views
 * — but a list only ever names a view, so the three stay one definition rather
 * than a combination each call site has to get right.
 */
export const TABLE_VIEWS: Record<
  TableView,
  {
    cellDisplay: TableCellDisplay;
    density: TableDensity;
    maxLines?: number;
  }
> = {
  compact: { cellDisplay: "truncate", density: "compact" },
  full: {
    cellDisplay: "wrap",
    density: "comfortable",
    maxLines: FULL_VIEW_MAX_LINES,
  },
  fit: { cellDisplay: "fit", density: "comfortable" },
};

const EMPTY: TablePreferences = {};

/**
 * The identity a record belongs to. `282` for a plain session, `282-as-9` while
 * that user is impersonating advertiser 9 — the account id stays the first
 * segment either way, so `ownerOf` can tell whose record it is without caring
 * which advertiser they were looking at.
 */
function identityOf(
  user: { id?: number | string; asAdvertiser?: number | string } | null
) {
  if (user?.id == null) return ANON;
  return user.asAdvertiser != null
    ? `${user.id}-as-${user.asAdvertiser}`
    : `${user.id}`;
}

/** The account id out of a full key, or null if it isn't one of ours. */
function ownerOf(key: string) {
  if (!key.startsWith(`${PREFIX}.`)) return null;
  // `<identity>.<tableId>` — tableId may itself contain dots (`lab.lineItems`),
  // the identity never does, so only the first segment is taken.
  const identity = key.slice(PREFIX.length + 1).split(".")[0];
  return identity.split("-as-")[0];
}

/**
 * Anything in localStorage is untrusted: it may predate the current shape, or
 * have been hand-edited. A stored `view` of `"wrap"` from an earlier iteration
 * would land straight in a `ToggleButtonGroup` as a value matching no button,
 * so read defensively and drop whatever doesn't fit rather than throwing on a
 * table render.
 */
function read(key: string | null): TablePreferences {
  if (!key) return EMPTY;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return EMPTY;
    // A record written by a shape this build does not know is discarded whole
    // rather than picked at. Missing means v1 — the shape versioning was added
    // to, so those records are read normally.
    if ((parsed.v ?? 1) !== VERSION) return EMPTY;
    const next: TablePreferences = {};
    if (VIEWS.includes(parsed.view)) next.view = parsed.view;
    if (
      Array.isArray(parsed.hiddenColumns) &&
      parsed.hiddenColumns.every((id: unknown) => typeof id === "string")
    ) {
      next.hiddenColumns = parsed.hiddenColumns;
    }
    return next;
  } catch {
    return EMPTY;
  }
}

function write(key: string | null, value: TablePreferences) {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify({ v: VERSION, ...value }));
  } catch {
    // Quota, or storage blocked outright in a private window. A remembered
    // column choice is not worth failing a render over — the table keeps
    // working, it just forgets.
  }
}

/**
 * Once per account per page load — every login and every impersonation switch
 * goes through `window.location.href`, so module state is the right lifetime.
 */
let prunedFor: string | null = null;

/**
 * Drops table records belonging to anyone else once a different account is in
 * session. Two reasons: an ops machine accumulates a record per account anyone
 * ever signed into or impersonated, and on a shared desktop the next person
 * should not inherit — or be able to read back — how the last one had their
 * tables arranged.
 *
 * Never while impersonating a user. `loginAsSomeUser` replaces the whole user
 * object, so `user.id` becomes THEIRS, and pruning then would delete the
 * operator's own layouts on their own machine. `PREVIOUS_USER_KEY` is exactly
 * the "this is a borrowed identity" flag; switching back runs the sweep with
 * the real id and clears up what the impersonated session left behind.
 */
function pruneForeignRecords(userId: number | string | undefined) {
  if (userId == null) return;
  if (window.localStorage.getItem(PREVIOUS_USER_KEY)) return;
  const mine = String(userId);
  if (prunedFor === mine) return;

  // The id React is rendering with has to still be the id in storage. Both the
  // teardown paths clear USER_KEY while React carries on holding the old user
  // for a moment, and in that window this would sweep under the wrong identity:
  //
  //   - `logout()` calls `setUser(null)` BEFORE assigning `window.location.href`
  //   - the 401 interceptor removes TOKEN, USER *and* PREVIOUS_USER and calls
  //     `window.location.reload()`, which queues a navigation without stopping
  //     script execution and never calls `setUser`
  //
  // The second is the dangerous one: it drops the borrowed-identity flag while
  // an impersonated user is still mounted, so any table mounting before the
  // reload lands would prune under THEIR id and delete the operator's own
  // layouts for every table in the app. Comparing against storage closes both,
  // and is a stronger check than the flag alone.
  let storedId: string | null = null;
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    storedId = raw ? String(JSON.parse(raw)?.id ?? "") : null;
  } catch {
    storedId = null;
  }
  if (storedId !== mine) return;

  prunedFor = mine;
  try {
    Object.keys(window.localStorage)
      .filter((key) => {
        const owner = ownerOf(key);
        // `anon` is not another account, it is the logged-out bucket this
        // module documents as shared and persistent — and `logout()` briefly
        // renders tables with no user, so a real layout can land in it. Sweeping
        // it would delete the only copy of what was just written.
        return owner !== null && owner !== ANON && owner !== mine;
      })
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage blocked. Nothing to clean up, and nothing worth failing over.
  }
}

export default function useTablePreferences(tableId?: string) {
  const { user } = useUserContext();
  const storageKey = tableId
    ? `${PREFIX}.${identityOf(user)}.${tableId}`
    : null;

  // In an effect, not during render: this writes. It only ever removes OTHER
  // accounts' records, so the read above is never racing it for anything it
  // cares about. Every table calls this hook; the module-level guard inside
  // makes all but the first a no-op.
  React.useEffect(() => {
    pruneForeignRecords(user?.id);
  }, [user?.id]);

  // The key changes when the user changes — logging in as an advertiser does it
  // without unmounting anything — so the stored value has to be re-read rather
  // than kept from the first render. Reconciled during render instead of in an
  // effect so the table never paints one frame of the previous user's columns.
  const [state, setState] = React.useState(() => ({
    key: storageKey,
    value: read(storageKey),
  }));
  if (state.key !== storageKey) {
    setState({ key: storageKey, value: read(storageKey) });
  }

  // Storage is touched in the callback body, never inside a `setState` updater.
  // An updater has to be pure: React double-invokes it under StrictMode, and may
  // discard and re-run an update while re-basing, so a read/write in there
  // happens a non-deterministic number of times. It is only harmless today
  // because every patch is absolute; the first patch computed from the previous
  // value would double-apply.
  const held = state.value;
  const update = React.useCallback(
    (patch: TablePreferences) => {
      // Three layers, in order. What this hook already holds is the floor;
      // storage wins over it; the patch wins over both.
      //
      // Storage has to beat the floor because two hooks can point at one record
      // — the view sits with the table while the column choice may sit with
      // whatever renders the picker — and a patch built on a stale snapshot
      // would quietly undo the other one's last write.
      //
      // The floor exists for the case where storage has nothing to say: another
      // tab signing in as a different account sweeps this record while this
      // session is still open on it. Without the floor the next click here reads
      // EMPTY and writes back only the field it changed, so the user's other
      // choices vanish mid-session with no action of their own.
      const value = { ...held, ...read(storageKey), ...patch };
      write(storageKey, value);
      setState({ key: storageKey, value });
    },
    [storageKey, held]
  );

  return [state.value, update] as const;
}
